import { createSlice } from '@reduxjs/toolkit';
import { Resource } from 'model/Resource';
import { addAsyncCasesNew } from 'shared/utils/redux';

import {
  createEmailTemplate,
  fetchEmailDistribution,
  getTranslatedEmailTemplate,
  scheduleEmailTemplate,
  scheduleEmailTemplateRelativeDate,
  unscheduleEmailTemplate,
  updateInvitationSendTime,
} from './asyncActions';
import { EmailDistributionState, STORE_NAME } from './initialState';

export const emailDistributionSlice = createSlice({
  name: STORE_NAME,
  initialState: EmailDistributionState.INITIAL_DOMAIN,
  reducers: {
    clearCurrentEmailTemplate: (state) => {
      state.currentEmailTemplate = null;
    },
    clearEmailDistribution: (state) => {
      state.distributionResource = EmailDistributionState.INITIAL_DOMAIN.distributionResource;
    },
    clearEmailTemplateByIdByLanguage: (state) => {
      state.emailTemplateByIdByLanguage =
        EmailDistributionState.INITIAL_DOMAIN.emailTemplateByIdByLanguage;
    },
  },
  extraReducers: (builder) => {
    addAsyncCasesNew(builder, fetchEmailDistribution, (state, action) => {
      state.distributionResource = action.payload;
    });

    builder.addCase(getTranslatedEmailTemplate.fulfilled, (state, action) => {
      const { emailTemplateId, language } = action.meta.arg;
      if (state.emailTemplateByIdByLanguage[emailTemplateId]) {
        state.emailTemplateByIdByLanguage[emailTemplateId][language] = action.payload;
      } else {
        state.emailTemplateByIdByLanguage[emailTemplateId] = {
          [language]: action.payload,
        } as EmailDistributionState.EmailTemplateByLanguage;
      }
    });

    builder.addCase(getTranslatedEmailTemplate.rejected, (state, action) => {
      const { emailTemplateId, language } = action.meta.arg;
      if (state.emailTemplateByIdByLanguage[emailTemplateId]) {
        state.emailTemplateByIdByLanguage[emailTemplateId][language] = null;
      } else {
        state.emailTemplateByIdByLanguage[emailTemplateId] = {
          [language]: null,
        } as EmailDistributionState.EmailTemplateByLanguage;
      }
    });

    builder.addCase(scheduleEmailTemplate.fulfilled, (state, action) => {
      const distribution = state.distributionResource.data;

      const updatedEmailTemplates = [distribution.invitation, ...(distribution.reminders || [])]
        .filter(Boolean)
        .map((emailTemplate) =>
          emailTemplate.id === action.meta.arg.emailTemplateId
            ? {
                ...emailTemplate,
                schedules: (() => {
                  const schedules = emailTemplate.schedules || [];
                  const existingIndex = schedules.findIndex((s) => s.id === action.payload.id);
                  if (existingIndex >= 0) {
                    const newSchedules = [...schedules];
                    newSchedules[existingIndex] = action.payload;
                    return newSchedules;
                  }
                  return [...schedules, action.payload];
                })(),
              }
            : emailTemplate,
        );

      // Inline previous setEmailTemplates behavior: derive invitation/reminders from the list
      const newInvitation =
        updatedEmailTemplates.find((et) => et.type === 'invitation') ??
        distribution.invitation ??
        null;
      const newReminders = updatedEmailTemplates.filter((et) => et.type === 'reminder');

      const updatedDistribution = {
        ...distribution,
        invitation: newInvitation,
        reminders: newReminders,
      };

      state.distributionResource = Resource.createResolvedResource({
        data: updatedDistribution,
      });
    });

    builder.addCase(scheduleEmailTemplateRelativeDate.fulfilled, (state, action) => {
      const distribution = state.distributionResource.data;

      const updatedEmailTemplates = [distribution.invitation, ...(distribution.reminders || [])]
        .filter(Boolean)
        .map((emailTemplate) =>
          emailTemplate.id === action.meta.arg.emailTemplateId
            ? {
                ...emailTemplate,
                scheduleRelativeDate: action.meta.arg.schedule.relativeDate,
              }
            : emailTemplate,
        );

      const newInvitation =
        updatedEmailTemplates.find((et) => et.type === 'invitation') ??
        distribution.invitation ??
        null;
      const newReminders = updatedEmailTemplates.filter((et) => et.type === 'reminder');

      const updatedDistribution = {
        ...distribution,
        invitation: newInvitation,
        reminders: newReminders,
      };

      state.distributionResource = Resource.createResolvedResource({
        data: updatedDistribution,
      });
    });

    builder.addCase(updateInvitationSendTime.fulfilled, (state, action) => {
      const distribution = state.distributionResource.data;
      if (
        !distribution?.invitation ||
        distribution.invitation.id !== action.meta.arg.emailTemplateId
      ) {
        return;
      }
      state.distributionResource = Resource.createResolvedResource({
        data: {
          ...distribution,
          invitation: {
            ...distribution.invitation,
            sendAtTime: action.meta.arg.sendAtTime,
          },
        },
      });
    });

    builder.addCase(unscheduleEmailTemplate.fulfilled, (state, action) => {
      const distribution = state.distributionResource.data;

      const updatedEmailTemplates = [distribution.invitation, ...(distribution.reminders || [])]
        .filter(Boolean)
        .map((emailTemplate) =>
          emailTemplate.id === action.meta.arg.emailTemplateId
            ? {
                ...emailTemplate,
                schedules: (emailTemplate.schedules || []).filter(
                  (schedule) => schedule.id !== action.meta.arg.scheduleId,
                ),
              }
            : emailTemplate,
        );

      // Inline previous setEmailTemplates behavior: derive invitation/reminders from the list
      const newInvitation =
        updatedEmailTemplates.find((et) => et.type === 'invitation') ??
        distribution.invitation ??
        null;
      const newReminders = updatedEmailTemplates.filter((et) => et.type === 'reminder');

      const updatedDistribution = {
        ...distribution,
        invitation: newInvitation,
        reminders: newReminders,
      };

      state.distributionResource = Resource.createResolvedResource({
        data: updatedDistribution,
      });
    });

    builder.addCase(createEmailTemplate.fulfilled, (state, action) => {
      state.currentEmailTemplate = action.payload;
    });
  },
});

export const {
  clearCurrentEmailTemplate,
  clearEmailDistribution,
  clearEmailTemplateByIdByLanguage,
} = emailDistributionSlice.actions;
